import React, { useState, useEffect } from 'react';
import axios from 'axios';

const OnlineApp = () => {
  // Form state
  const [certificateType, setCertificateType] = useState('');
  const [formData, setFormData] = useState({
    // Common fields
    token_id: '',
    local_no: '',
    date: '',
    name: '',
    father_name: '',
    cnic: '',
    phone_no: '',
    dob: '',
    remarks: '',
    
    // Domicile Certificate specific
    address: '',
    district: '',
    province: '',
    date_of_arrival: '',
    marital_status: '',
    wife_name: '',
    husband_name: '',
    sons_ages: '',
    daughters_ages: '',
    trade_occupation: '',
    marks_of_identification: '',
    
    // FIR specific
    criminal_loc: '',
    crime: '',
    place: '',
    
    // Mutation specific
    operation: '',
    status_f: '',
    witness: '',
    crime_news: '',
    
    // Fard specific
    sr_no: '',
    rakba: '',
    paid_quiry: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: '', isError: false });
  const [direction, setDirection] = useState('right');

  // Certificate options
  const certificateOptions = [
    'Domicile Certificate',
    'Local Certificate',
    'Birth Certificate',
    'Death Certificate',
    'Marriage Certificate',
    'Passport',
    'Arms License',
    'FIR',
    'Mutation',
    'Fard'
  ];

  // Generate random 8-digit token ID with specified format
  const generateTokenId = () => {
    const randomNum = Math.floor(10000000 + Math.random() * 90000000);
    return `Online-${randomNum}`;
  };

  // Handle certificate type change
  const handleCertificateTypeChange = (e) => {
    const selectedType = e.target.value;
    setCertificateType(selectedType);
    
    // Reset form with new token ID but preserve local_no if already entered
    setFormData(prev => ({
      // Reset all fields except local_no
      token_id: generateTokenId(),
      local_no: prev.local_no || '',
      date: '',
      name: '',
      father_name: '',
      cnic: '',
      phone_no: '',
      dob: '',
      remarks: '',
      address: '',
      district: '',
      province: '',
      date_of_arrival: '',
      marital_status: '',
      wife_name: '',
      husband_name: '',
      sons_ages: '',
      daughters_ages: '',
      trade_occupation: '',
      marks_of_identification: '',
      criminal_loc: '',
      crime: '',
      place: '',
      operation: '',
      status_f: '',
      witness: '',
      crime_news: '',
      sr_no: '',
      rakba: '',
      paid_quiry: ''
    }));
    setSubmitMessage({ text: '', isError: false });
  };

  // Generate token ID when component mounts and when certificate type changes
  useEffect(() => {
    if (certificateType) {
      setFormData(prev => ({
        ...prev,
        token_id: generateTokenId()
      }));
    }
  }, [certificateType]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form fields
  const validateForm = () => {
    if (!certificateType) {
      setSubmitMessage({ text: 'Please select a certificate type', isError: true });
      return false;
    }

    // Basic validation - you can expand this for each certificate type
    if (!formData.token_id || !formData.date) {
      setSubmitMessage({ text: 'Please fill all required fields', isError: true });
      return false;
    }

    // CNIC validation (13 digits)
    if (formData.cnic && !/^\d{13}$/.test(formData.cnic)) {
      setSubmitMessage({ text: 'CNIC must be exactly 13 digits', isError: true });
      return false;
    }

    // Phone validation (11 digits)
    if (formData.phone_no && !/^\d{11}$/.test(formData.phone_no)) {
      setSubmitMessage({ text: 'Phone number must be exactly 11 digits', isError: true });
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
  
    setIsSubmitting(true);
    setSubmitMessage({ text: '', isError: false });
  
    try {
      const response = await axios.post(
        'https://chaman.gob.pk/eac_backend/onlineapplication.php',
        {
          certificate_type: certificateType,
          ...formData
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: false
        }
      );
      
      // Handle success
      setSubmitMessage({ 
        text: `Application submitted successfully! Your Token ID: ${formData.token_id}`, 
        isError: false 
      });
      
      // Reset form after successful submission
      setFormData({
        token_id: generateTokenId(),
        local_no: '',
        date: '',
        name: '',
        father_name: '',
        cnic: '',
        phone_no: '',
        dob: '',
        remarks: '',
        address: '',
        district: '',
        province: '',
        date_of_arrival: '',
        marital_status: '',
        wife_name: '',
        husband_name: '',
        sons_ages: '',
        daughters_ages: '',
        trade_occupation: '',
        marks_of_identification: '',
        criminal_loc: '',
        crime: '',
        place: '',
        operation: '',
        status_f: '',
        witness: '',
        crime_news: '',
        sr_no: '',
        rakba: '',
        paid_quiry: ''
      });
      setCertificateType('');
      
    } catch (error) {
      let errorMsg = 'Error submitting form. Please try again.';
      
      if (error.response) {
        errorMsg = error.response.data?.message || 
                  `Server error (${error.response.status}): ${error.response.statusText}`;
      } else if (error.request) {
        errorMsg = 'No response from server. Check your connection.';
      } else {
        errorMsg = 'Request setup failed. Please try again.';
      }
      
      setSubmitMessage({ 
        text: errorMsg, 
        isError: true 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="online-app-container">
      {/* Moving text reference */}
      <div className="moving-text-container">
        <div className={`moving-text ${direction}`}>
          <span>E-Administration Chaman</span>
        </div>
      </div>

      <div className="app-sections">
        {/* Left Section with background image */}
        <div className="lf-sect">
          <div className="info-card">
            <div className="overlay-content">
              <div className="logo">E-AC</div>
              <h2>Online Certificate Application</h2>
              <p className="description">
                Apply for various certificates online with our streamlined process. 
                Save time and avoid queues by submitting your application digitally.
              </p>
            </div>
            
            <div className="guidelines">
              <h3>Application Guidelines</h3>
              <ul>
                <li>Fill all required fields accurately</li>
                <li>Ensure CNIC and phone numbers are correct</li>
                <li>Double-check dates before submission</li>
                <li>Save your Token ID for future reference</li>
                <li>Processing time: 3-5 working days</li>
                <li>You'll receive SMS notifications</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="rt-sect">
          <div className="form-card">
            <h2>Application Form</h2>
            <form onSubmit={handleSubmit}>
              {/* Certificate Type Selection */}
              <div className="form-group">
                <label htmlFor="certificateType">Certificate Type *</label>
                <select
                  id="certificateType"
                  value={certificateType}
                  onChange={handleCertificateTypeChange}
                  className={submitMessage.isError && !certificateType ? 'input-error' : ''}
                  required
                >
                  <option value="">-- Select Certificate Type --</option>
                  {certificateOptions.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Dynamic Form Fields */}
              {certificateType && (
                <>
                  {/* Token ID Field - Auto-generated and read-only */}
                  <div className="form-group">
                    <label htmlFor="token_id">Token ID *</label>
                    <input
                      type="text"
                      id="token_id"
                      name="token_id"
                      value={formData.token_id}
                      readOnly
                      className="read-only-field"
                      required
                    />
                    <small className="hint">Auto-generated</small>
                  </div>

                  {/* Local Number Field */}
                  {['Domicile Certificate', 'Local Certificate', 'Birth Certificate', 
                    'Death Certificate', 'Marriage Certificate'].includes(certificateType) && (
                    <div className="form-group">
                      <label htmlFor="local_no">Local Number *</label>
                      <input
                        type="text"
                        id="local_no"
                        name="local_no"
                        value={formData.local_no}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  )}

                  {/* Date Field */}
                  <div className="form-group">
                    <label htmlFor="date">Date of Application *</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Name Field */}
                  <div className="form-group">
                    <label htmlFor="name">Applicant Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Father Name Field */}
                  {['Domicile Certificate', 'Local Certificate', 'Birth Certificate', 
                    'Death Certificate', 'Marriage Certificate', 'Passport', 'Arms License'].includes(certificateType) && (
                    <div className="form-group">
                      <label htmlFor="father_name">Father's/Husband's Name *</label>
                      <input
                        type="text"
                        id="father_name"
                        name="father_name"
                        value={formData.father_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  )}

                  {/* CNIC Field */}
                  {['Domicile Certificate', 'Local Certificate', 'Birth Certificate', 
                    'Death Certificate', 'Marriage Certificate', 'Passport', 'Arms License'].includes(certificateType) && (
                    <div className="form-group">
                      <label htmlFor="cnic">CNIC Number *</label>
                      <input
                        type="text"
                        id="cnic"
                        name="cnic"
                        value={formData.cnic}
                        onChange={handleInputChange}
                        maxLength="13"
                        placeholder="1234512345678"
                        required
                      />
                      <small className="hint">13 digits without dashes</small>
                    </div>
                  )}

                  {/* Phone Number Field */}
                  {['Domicile Certificate', 'Local Certificate', 'Birth Certificate', 
                    'Death Certificate', 'Marriage Certificate', 'Passport', 'Arms License', 'FIR', 'Mutation'].includes(certificateType) && (
                    <div className="form-group">
                      <label htmlFor="phone_no">Mobile Number *</label>
                      <input
                        type="tel"
                        id="phone_no"
                        name="phone_no"
                        value={formData.phone_no}
                        onChange={handleInputChange}
                        maxLength="11"
                        placeholder="03001234567"
                        required
                      />
                      <small className="hint">11 digits without dashes</small>
                    </div>
                  )}

                  {/* Date of Birth Field */}
                  {['Birth Certificate', 'Death Certificate', 'Marriage Certificate'].includes(certificateType) && (
                    <div className="form-group">
                      <label htmlFor="dob">Date of Birth *</label>
                      <input
                        type="date"
                        id="dob"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  )}

                  {/* Domicile Certificate Specific Fields */}
                  {certificateType === 'Domicile Certificate' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="address">Current Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="district">District *</label>
                        <input
                          type="text"
                          id="district"
                          name="district"
                          value={formData.district}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="province">Province *</label>
                        <input
                          type="text"
                          id="province"
                          name="province"
                          value={formData.province}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="date_of_arrival">Date of Arrival *</label>
                        <input
                          type="date"
                          id="date_of_arrival"
                          name="date_of_arrival"
                          value={formData.date_of_arrival}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="marital_status">Marital Status *</label>
                        <select
                          id="marital_status"
                          name="marital_status"
                          value={formData.marital_status}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">-- Select Marital Status --</option>
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                          <option value="Divorced">Divorced</option>
                          <option value="Widowed">Widowed</option>
                        </select>
                      </div>

                      {formData.marital_status === 'Married' && (
                        <>
                          <div className="form-group">
                            <label htmlFor="wife_name">Wife's Name</label>
                            <input
                              type="text"
                              id="wife_name"
                              name="wife_name"
                              value={formData.wife_name}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="husband_name">Husband's Name</label>
                            <input
                              type="text"
                              id="husband_name"
                              name="husband_name"
                              value={formData.husband_name}
                              onChange={handleInputChange}
                            />
                          </div>
                        </>
                      )}

                      <div className="form-group">
                        <label htmlFor="sons_ages">Sons Names and Ages</label>
                        <input
                          type="text"
                          id="sons_ages"
                          name="sons_ages"
                          value={formData.sons_ages}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="daughters_ages">Daughters Names and Ages</label>
                        <input
                          type="text"
                          id="daughters_ages"
                          name="daughters_ages"
                          value={formData.daughters_ages}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="trade_occupation">Trade/Occupation *</label>
                        <input
                          type="text"
                          id="trade_occupation"
                          name="trade_occupation"
                          value={formData.trade_occupation}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="marks_of_identification">Special Marks of Identification *</label>
                        <textarea
                          id="marks_of_identification"
                          name="marks_of_identification"
                          value={formData.marks_of_identification}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* FIR Specific Fields */}
                  {certificateType === 'FIR' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="criminal_loc">Location of Crime *</label>
                        <input
                          type="text"
                          id="criminal_loc"
                          name="criminal_loc"
                          value={formData.criminal_loc}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="crime">Type of Crime *</label>
                        <input
                          type="text"
                          id="crime"
                          name="crime"
                          value={formData.crime}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="place">Place of Incident *</label>
                        <input
                          type="text"
                          id="place"
                          name="place"
                          value={formData.place}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="remarks">Incident Details *</label>
                        <textarea
                          id="remarks"
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Mutation Specific Fields */}
                  {certificateType === 'Mutation' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="operation">Operation Type *</label>
                        <input
                          type="text"
                          id="operation"
                          name="operation"
                          value={formData.operation}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="status_f">Current Status *</label>
                        <input
                          type="text"
                          id="status_f"
                          name="status_f"
                          value={formData.status_f}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="witness">Witness Name *</label>
                        <input
                          type="text"
                          id="witness"
                          name="witness"
                          value={formData.witness}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="crime_news">Crime Details *</label>
                        <input
                          type="text"
                          id="crime_news"
                          name="crime_news"
                          value={formData.crime_news}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Fard Specific Fields */}
                  {certificateType === 'Fard' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="sr_no">Serial Number *</label>
                        <input
                          type="text"
                          id="sr_no"
                          name="sr_no"
                          value={formData.sr_no}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="place">Property Location *</label>
                        <input
                          type="text"
                          id="place"
                          name="place"
                          value={formData.place}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="name">Owner Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="rakba">Land Area (Rakba) *</label>
                        <input
                          type="text"
                          id="rakba"
                          name="rakba"
                          value={formData.rakba}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="paid_quiry">Paid Inquiry Amount *</label>
                        <input
                          type="text"
                          id="paid_quiry"
                          name="paid_quiry"
                          value={formData.paid_quiry}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Processing...
                      </>
                    ) : 'Submit Application'}
                  </button>
                </>
              )}

              {/* Status Message */}
              {submitMessage.text && (
                <div className={`status-message ${submitMessage.isError ? 'error' : 'success'}`}>
                  {submitMessage.text}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineApp;